import { useMemo } from 'react';
import { useCartItemsStatusQuery, type ItemStatus } from '../types/front.graphql';
import { INSUFFICIENT_STOCK_CODE, OUT_OF_STOCK_CODE } from '../constants/cart.constants';

/**
 * Hook to get cart items stock status with automatic mapping by SKU
 * Note: When products are in stock, they won't appear in the query result
 * @param cartId - The cart ID to get stock status for
 * @returns Object containing stockStatus, loading, error, refetch function, and utility functions for stock checking
 */

type StockStatus = Record<string, ItemStatus>;

export function useCartItemsStockStatus(cartId: string) {
    const { data, loading, error, refetch } = useCartItemsStatusQuery({
        variables: {
            cartId,
        },
        fetchPolicy: 'network-only',
        skip: !cartId,
    });

    const items = useMemo(() => {
        return (data?.cartItemsStatus || []).filter(Boolean) as ItemStatus[];
    }, [data]);

    const stockStatus: StockStatus = useMemo(() => {
        return items.reduce<StockStatus>((accumulator, item) => {
            if (item?.sku) accumulator[item.sku] = item;
            return accumulator;
        }, {});
    }, [items]);

    /**
     * Get stock status code for a given SKU
     * @param sku - Product SKU
     * @returns Stock status code (undefined = in stock, 102 = out of stock, 103 = insufficient stock)
     */
    const getStockCode = (sku: string): number | undefined => {
        if (!sku.trim()) {
            return undefined;
        }
        return stockStatus[sku]?.stock_status?.code;
    };

    /**
     * Check if a product is out of stock
     * @param sku - Product SKU
     * @returns true if out of stock (code 102), false otherwise
     */
    const isOutOfStock = (sku: string): boolean => {
        if (!sku.trim()) {
            return false;
        }
        const code = getStockCode(sku);
        return code === OUT_OF_STOCK_CODE;
    };

    /**
     * Check if a product has insufficient stock
     * @param sku - Product SKU
     * @returns true if insufficient stock (code 103), false otherwise
     */
    const isInsufficientStock = (sku: string): boolean => {
        if (!sku.trim()) {
            return false;
        }
        const code = getStockCode(sku);
        return code === INSUFFICIENT_STOCK_CODE;
    };

    /**
     * Check if a product is in stock (no issues)
     * @param sku - Product SKU
     * @returns true if in stock (no status code), false otherwise
     */
    const isInStock = (sku: string): boolean => {
        if (!sku.trim()) {
            return false;
        }
        const code = getStockCode(sku);
        return code === undefined;
    };

    /**
     * Get full stock status object for a given SKU
     * @param sku - Product SKU
     * @returns ItemStatus object or undefined if not found
     */
    const getStockStatus = (sku: string): ItemStatus | undefined => {
        if (!sku.trim()) {
            return undefined;
        }
        return stockStatus[sku];
    };

    return {
        stockStatus,
        loading,
        error,
        refetch,
        getStockCode,
        isInStock,
        isOutOfStock,
        isInsufficientStock,
        getStockStatus,
    };
}
